import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { SyncLoader } from 'react-spinners';
import { StepCard } from '../components/StepCard';
import { setSelectedSlot, setSelectedOperatories, setViewMode, setSlotsFetchDurationOverride, setIsFirstSlotsFetch } from '../redux/slices/appointmentSlice';
import TimeSlotSelection from '../components/TimeSlotSelection';
import { formatDateToISO, isSlotInFuture } from '../utils/helpers';
import { useIsSmallScreen } from '../hooks/useIsSmallScreen';
import { AvailableSlotsResponse, fetchAvailableSlots } from '../services/api';
import { SMALL_SCREEN_DAYS_TO_SHOW, LARGE_SCREEN_DAYS_TO_SHOW, MIN_SLOTS_THRESHOLD, EXTENDED_SLOTS_DAYS, DEFAULT_SLOTS_LOOKUP_MONTHS } from '../utils/constants';

export const AvailableSlots = () => {
  const dispatch = useDispatch();
  const patientType = useSelector((s: RootState) => s.appointment.patientType);
  const selectedTreatment = useSelector((s: RootState) => s.appointment.treatment);
  const selectedDurationMinutes = useSelector((s: RootState) => s.appointment.selectedDurationMinutes);
  const selectedSlot = useSelector((s: RootState) => s.appointment.selectedSlot);
  const { practice } = useSelector((s: RootState) => s.auth);
  const primetimeFilter = useSelector((s: RootState) => s.appointment.primetimeFilter);
  const { viewMode, slotsFetchDurationOverride, isFirstSlotsFetch } = useSelector((s: RootState) => s.appointment);

  const [loading, setLoading] = useState<boolean>(true);
  const [currentStart, setCurrentStart] = useState<Date>(new Date());
  const [slotsByDate, setSlotsByDate] = useState<Record<string, Record<string, string[]>>>({});

  // Infinite Scroll States
  const [listVisibleDates, setListVisibleDates] = useState<Date[]>([]);
  const [listCursorDate, setListCursorDate] = useState<Date>(new Date());
  const [accumulatedSlots, setAccumulatedSlots] = useState<Record<string, Record<string, string[]>>>({});
  const [isListLoading, setIsListLoading] = useState(false);
  const [searchLimitReached, setSearchLimitReached] = useState(false);
  const requestIdRef = useRef(0);

  const isSmallScreen = useIsSmallScreen();

  const daysToShow = useMemo(() => {
    if (slotsFetchDurationOverride) return slotsFetchDurationOverride;
    return isSmallScreen ? SMALL_SCREEN_DAYS_TO_SHOW : LARGE_SCREEN_DAYS_TO_SHOW;
  }, [isSmallScreen, slotsFetchDurationOverride]);

  const currentEnd = useMemo(() => {
    const d = new Date(currentStart);
    d.setDate(d.getDate() + (daysToShow - 1));
    return d;
  }, [currentStart, daysToShow]);

  const goPrevWeek = () => {
    const d = new Date(currentStart);
    d.setDate(d.getDate() - daysToShow);
    setCurrentStart(d);
  };

  const goNextWeek = () => {
    const d = new Date(currentStart);
    d.setDate(d.getDate() + daysToShow);
    setCurrentStart(d);
  };

  const parseSlotsResponse = (res: AvailableSlotsResponse, bufferMinutes: number, timeZone?: string) => {
    const map: Record<string, Record<string, string[]>> = {};
    let totalSlotsCount = 0;
    const datesWithSlots = new Set<string>();

    for (const operatory of res || []) {
      const operatoryID = operatory?.id;
      const available = operatory?.attributes?.available_slots || {};
      for (const dateKey of Object.keys(available)) {
        const items = available[dateKey] || [];

        // Only count as valid if it has at least one FUTURE slot
        const futureSlots = items.filter(item => isSlotInFuture(dateKey, item.start_time, bufferMinutes, timeZone));

        if (futureSlots.length > 0) {
          datesWithSlots.add(dateKey);
        }

        if (!map[dateKey]) map[dateKey] = {};
        for (const item of futureSlots) {
          const display = item.start_time;
          if (!map[dateKey][display]) {
            map[dateKey][display] = [];
            totalSlotsCount++;
          }
          map[dateKey][display].push(operatoryID);
        }
      }
    }
    return { map, totalSlotsCount, datesWithSlots: Array.from(datesWithSlots).sort() };
  };

  const bufferMinutes = selectedTreatment?.bufferMinutes || 0;
  const availableSlotsLookupMonths = selectedTreatment?.availableSlotsLookupMonths || DEFAULT_SLOTS_LOOKUP_MONTHS;
  const timeZone = practice?.time_zone;

  // The furthest date we are allowed to request/show slots for: today + the treatment's lookup window.
  // Both the table "next" button and the list infinite-scroll stop at this cutoff.
  const maxLookupDate = useMemo(() => {
    const base = timeZone ? moment().tz(timeZone) : moment();
    return base.add(availableSlotsLookupMonths, 'months').toDate();
  }, [timeZone, availableSlotsLookupMonths]);
  const maxLookupDateStr = useMemo(() => formatDateToISO(maxLookupDate), [maxLookupDate]);

  const fetchMoreSlots = useCallback(async (startDate: Date, currentListDates: Date[], isInitialListLoad = false) => {
    const reqId = ++requestIdRef.current;
    if (isInitialListLoad) {
      setLoading(true);
      setListVisibleDates([]);
      setAccumulatedSlots({});
      setSearchLimitReached(false);
    } else {
      setIsListLoading(true);
    }

    try {
      let fetchedDates: Date[] = [];
      let nextDate = new Date(startDate);

      // If initial, target 15 days. If loading more, target 7 more than we currently have.
      const targetCount = (isInitialListLoad ? 0 : currentListDates.length) + (isInitialListLoad ? 15 : 7);

      // Keep fetching 30-day chunks until we have enough dates or we reach the lookup cutoff.
      while ((isInitialListLoad ? fetchedDates.length : (currentListDates.length + fetchedDates.length)) < targetCount && formatDateToISO(nextDate) <= maxLookupDateStr) {
        if (requestIdRef.current !== reqId) return;

        const end = new Date(nextDate);
        end.setDate(end.getDate() + 30);
        // Clamp the final chunk so we never request a date past the cutoff.
        if (end > maxLookupDate) {
          end.setTime(maxLookupDate.getTime());
        }

        const res: AvailableSlotsResponse = await fetchAvailableSlots({
          start_date: formatDateToISO(nextDate),
          end_date: formatDateToISO(end),
          new_patient: patientType === 'new',
          fixed_duration_slots: true,
          appointment_type_id: selectedTreatment?.id,
          ...(practice?.promote_primetime && { filters: primetimeFilter }),
        });

        const { map, datesWithSlots } = parseSlotsResponse(res, bufferMinutes, timeZone);

        setAccumulatedSlots(prev => ({ ...prev, ...map }));

        const validDateObjs = datesWithSlots.map(ds => {
          const parts = ds.split('-').map(Number);
          return new Date(parts[0], parts[1] - 1, parts[2]);
        });

        fetchedDates = [...fetchedDates, ...validDateObjs];

        nextDate = new Date(end);
        nextDate.setDate(nextDate.getDate() + 1);
      }

      if (requestIdRef.current !== reqId) return;

      // Once the cursor passes the cutoff there is nothing more to load, so stop the
      // infinite scroll. The "no slots" empty state only shows if we also found nothing.
      if (formatDateToISO(nextDate) > maxLookupDateStr) {
        setSearchLimitReached(true);
      }

      const finalListDates = isInitialListLoad ? fetchedDates : [...currentListDates, ...fetchedDates];

      setListVisibleDates(finalListDates);
      setListCursorDate(nextDate);

    } finally {
      if (requestIdRef.current === reqId) {
        setLoading(false);
        setIsListLoading(false);
      }
    }
  }, [patientType, selectedTreatment?.id, practice?.promote_primetime, primetimeFilter, bufferMinutes, timeZone, maxLookupDate, maxLookupDateStr]);

  const lastFetchParamsRef = useRef<string>('');
  const isFirstRun = useRef(true);

  // Handle "Load More" from UI
  const handleLoadMore = () => {
    if (!isListLoading && !searchLimitReached) {
      fetchMoreSlots(listCursorDate, listVisibleDates, false);
    }
  };

  useEffect(() => {
    const currentParamsKey = JSON.stringify({
      start: formatDateToISO(currentStart),
      end: formatDateToISO(currentEnd),
      patientType,
      treatmentId: selectedTreatment?.id,
      primetimeFilter,
    });

    // We need a fresh decision if Redux says so OR if this is the first mount of the component instance
    const needsDecision = isFirstSlotsFetch || isFirstRun.current;

    const load = async () => {
      // 1. Established List View Branch
      // If we are already in list mode and don't need to re-evaluate (e.g. infinite scroll was already initialized)
      if (viewMode === 'list' && !needsDecision) {
        requestIdRef.current++;
        fetchMoreSlots(currentStart, [], true);
        return;
      }

      // 2. Initial Decision / Table View Branch
      // Deduplicate: skip if params haven't changed and we aren't waiting for a decision
      if (lastFetchParamsRef.current === currentParamsKey && !needsDecision) {
        return;
      }

      setLoading(true);
      const reqId = ++requestIdRef.current;

      try {
        // ALWAYS use the standard window (7 or 3 days) for the decision fetch,
        // regardless of whether we are currently "stored" in List mode (30 days).
        const decisionDays = isSmallScreen ? SMALL_SCREEN_DAYS_TO_SHOW : LARGE_SCREEN_DAYS_TO_SHOW;
        const decisionEnd = new Date(currentStart);
        decisionEnd.setDate(decisionEnd.getDate() + (decisionDays - 1));

        const res: AvailableSlotsResponse = await fetchAvailableSlots({
          start_date: formatDateToISO(currentStart),
          end_date: formatDateToISO(decisionEnd),
          new_patient: patientType === 'new',
          fixed_duration_slots: true,
          appointment_type_id: selectedTreatment?.id,
          ...(practice?.promote_primetime && { filters: primetimeFilter }),
        });

        if (requestIdRef.current !== reqId) return;

        const { map, totalSlotsCount } = parseSlotsResponse(res, bufferMinutes, timeZone);
        lastFetchParamsRef.current = currentParamsKey;

        if (needsDecision) {
          if (totalSlotsCount < MIN_SLOTS_THRESHOLD) {
            // SWITCHING TO/STAYING IN LIST: 
            dispatch(setViewMode('list'));
            dispatch(setSlotsFetchDurationOverride(EXTENDED_SLOTS_DAYS));
            dispatch(setIsFirstSlotsFetch(false));
            isFirstRun.current = false;

            // Chain the list expansion fetch immediately and await it
            await fetchMoreSlots(currentStart, [], true);
          } else {
            // SWITCHING TO/STAYING IN TABLE:
            dispatch(setViewMode('table'));
            dispatch(setSlotsFetchDurationOverride(null));
            setSlotsByDate(map);
            dispatch(setIsFirstSlotsFetch(false));
            isFirstRun.current = false;
          }
        } else {
          // Normal Table View update (e.g. filtering)
          setSlotsByDate(map);
        }
      } finally {
        if (requestIdRef.current === reqId) {
          setLoading(false);
        }
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStart, currentEnd, patientType, selectedTreatment?.id, selectedDurationMinutes, primetimeFilter, dispatch, viewMode, isFirstSlotsFetch]);

  const isPrevDisabled = useMemo(() => {
    if (!practice?.time_zone) return false;
    const practiceNow = moment().tz(practice.time_zone);
    const todayStr = practiceNow.format('YYYY-MM-DD');
    const currentStartStr = formatDateToISO(currentStart);
    return currentStartStr <= todayStr;
  }, [currentStart, practice?.time_zone]);

  // Disable "next" once the visible window reaches the lookup cutoff (mirrors isPrevDisabled).
  const isNextDisabled = useMemo(() => {
    return formatDateToISO(currentEnd) >= maxLookupDateStr;
  }, [currentEnd, maxLookupDateStr]);

  return (
    <StepCard>
      {loading ? (
        <div className="flex items-center justify-center min-h-[40vh]">
          <SyncLoader />
        </div>
      ) : (
        <TimeSlotSelection
          selectedSlot={selectedSlot}
          onSlotSelect={(slot) => dispatch(setSelectedSlot(slot))}
          onOperatoriesSelect={(ops) => dispatch(setSelectedOperatories(ops))}
          slotsByDate={viewMode === 'list' ? accumulatedSlots : slotsByDate}
          onPrevWeek={goPrevWeek}
          onNextWeek={goNextWeek}
          currentStart={currentStart}
          daysToShow={daysToShow}
          viewMode={viewMode || 'list'}
          isPrevDisabled={isPrevDisabled}
          isNextDisabled={isNextDisabled}

          // Infinite Scroll Props
          onLoadMore={handleLoadMore}
          isLoadingMore={isListLoading}
          searchLimitReached={searchLimitReached}
          visibleDates={viewMode === 'list' ? listVisibleDates : undefined} // Override for List View
        />
      )}
    </StepCard>
  );
};
